import React, { useMemo } from "react";

import { useWatch } from "antd/es/form/Form";
import Radio from "antd/es/radio";
import Switch from "antd/es/switch";
import { defineMessages, useIntl } from "react-intl";

import FieldVisibility, { FieldTeamVisibility } from "@mapmycustomers/shared/enum/FieldVisibility";
import { EntityTypesSupportingFieldCustomization } from "@mapmycustomers/shared/types/entity";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import { Alert } from "@mapmycustomers/ui";

import RadioGroup from "@app/component/input/RadioGroup";
import DefaultCustomFieldFormValues from "@app/scene/settings/component/CustomFields/type/DefaultCustomFieldFormValues";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { getFieldVisibilityName } from "@app/util/ui";

import styles from "./VisibilityRadioGroup.module.scss";

const messages = defineMessages({
  noAccessWarning: {
    id: "settings.manageFields.editPane.fieldAccess.noAccess.warning",
    defaultMessage:
      'The following field(s) cannot be marked as "No access" and <b>will not be updated</b> if you proceed with this change:',
    description: "warning message for bulk edit when marked as no access",
  },
  owners: {
    id: "settings.manageFields.editPane.fieldAccess.note.teamOwner",
    defaultMessage: "Team Owners can always edit any field",
    description: "Settings Custom field - create/edit - share&save Field Access",
  },
  privateForCalculatedField: {
    id: "settings.manageFields.editPane.fieldAccess.note.privateForCalculatedField",
    defaultMessage: "Only Team Owners can view",
    description: "Settings Custom field - create/edit - Only Team Owners can view",
  },
  privateWarning: {
    id: "settings.manageFields.editPane.fieldAccess.private.warning",
    defaultMessage:
      "The following field(s) cannot be marked as private and <b>will not be updated</b> if you proceed with this change:",
    description: "warning message for bulk edit when marked as private",
  },
  specifyByRole: {
    id: "settings.manageFields.editPane.fieldAccess.teams.specifyByRole",
    defaultMessage: "Specify by role only",
    description: "Label of the Specify by Role switch in manage field panel",
  },
  teams: {
    id: "settings.manageFields.editPane.fieldAccess.teams",
    defaultMessage: "Specific roles and/or teams",
    description: "Settings Custom field access - Specific roles and/or teams",
  },
});

interface Props {
  calculatedField?: boolean;
  disabledFieldVisibilities?: FieldTeamVisibility[];
  entityType: EntityTypesSupportingFieldCustomization;
  onChange?: (visibility: FieldVisibility) => void;
  onChangeSpecifyByRole?: (specifyByRole: boolean) => void;
  phiEnabled: boolean;
  selectedSchemaFields?: SchemaField[];
  value?: FieldVisibility;
}

const VisibilityRadioGroup: React.FC<Props> = ({
  calculatedField,
  disabledFieldVisibilities,
  entityType,
  onChange,
  onChangeSpecifyByRole,
  phiEnabled,
  selectedSchemaFields,
  value,
}) => {
  const intl = useIntl();

  const specifyByRoleChecked = useWatch("unifiedVisibility");
  const roleVisibility: DefaultCustomFieldFormValues["roleVisibility"] | undefined =
    useWatch("roleVisibility");
  const teamVisibility: DefaultCustomFieldFormValues["teamVisibility"] | undefined =
    useWatch("teamVisibility");

  const fieldModel = getFieldModelByEntityType(entityType);
  const fieldsNotSupportingPrivateVisibility = useMemo(
    () =>
      selectedSchemaFields
        ? selectedSchemaFields
            .filter((schemaField) => {
              const field = fieldModel.getByPlatformName(schemaField.field);
              return !field?.doesSupportPhi;
            })
            .map((schemaField) => schemaField.field)
        : [],
    [fieldModel, selectedSchemaFields]
  );

  const showAlertForTeamOrRoleVisibility = useMemo(() => {
    let showAlert = false;
    if (specifyByRoleChecked) {
      if (roleVisibility) {
        showAlert = Object.entries(roleVisibility).some(
          ([, visibility]) => visibility === FieldVisibility.PRIVATE
        );
      }
    } else {
      if (teamVisibility) {
        showAlert = Object.entries(teamVisibility).some(
          ([, record]) => record.visibility === FieldVisibility.PRIVATE
        );
      }
    }
    return (
      showAlert &&
      !phiEnabled &&
      value === FieldVisibility.TEAMS &&
      fieldsNotSupportingPrivateVisibility.length > 0
    );
  }, [
    fieldsNotSupportingPrivateVisibility,
    phiEnabled,
    roleVisibility,
    specifyByRoleChecked,
    teamVisibility,
    value,
  ]);

  return (
    <RadioGroup className={styles.container} onChange={onChange} value={value}>
      {!calculatedField && !phiEnabled && (
        <Radio
          disabled={disabledFieldVisibilities?.includes(FieldVisibility.FULL_ACCESS)}
          value={FieldVisibility.FULL_ACCESS}
        >
          {getFieldVisibilityName(intl, FieldVisibility.FULL_ACCESS)}
        </Radio>
      )}

      {!phiEnabled && (
        <Radio
          disabled={disabledFieldVisibilities?.includes(FieldVisibility.PUBLIC)}
          value={FieldVisibility.PUBLIC}
        >
          {getFieldVisibilityName(intl, FieldVisibility.PUBLIC)}
        </Radio>
      )}

      {value === FieldVisibility.PUBLIC && !calculatedField && !phiEnabled && (
        <div className={styles.note}>{intl.formatMessage(messages.owners)}</div>
      )}

      <Radio
        disabled={disabledFieldVisibilities?.includes(FieldVisibility.PRIVATE)}
        value={FieldVisibility.PRIVATE}
      >
        {getFieldVisibilityName(intl, FieldVisibility.PRIVATE)}
      </Radio>
      {value === FieldVisibility.PRIVATE && calculatedField && (
        <div className={styles.note}>{intl.formatMessage(messages.privateForCalculatedField)}</div>
      )}
      {!phiEnabled &&
        value === FieldVisibility.PRIVATE &&
        fieldsNotSupportingPrivateVisibility.length > 0 && (
          <Alert
            className={styles.alert}
            message={
              <>
                {intl.formatMessage(messages.privateWarning, { b: (text) => <b>{text}</b> })}
                <ul>
                  {fieldsNotSupportingPrivateVisibility.map((field) => (
                    <li key={field}>{fieldModel.getByPlatformName(field)?.displayName ?? field}</li>
                  ))}
                </ul>
              </>
            }
            showIcon
            type="warning"
          />
        )}
      <Radio className={styles.item} value={FieldVisibility.TEAMS}>
        <div className={styles.teamsLabel}>
          {intl.formatMessage(messages.teams)}
          {value === FieldVisibility.TEAMS && (
            <label className={styles.specifyByRoleLabel}>
              {intl.formatMessage(messages.specifyByRole)}
              <Switch checked={specifyByRoleChecked} onChange={onChangeSpecifyByRole} />
            </label>
          )}
        </div>
      </Radio>

      {showAlertForTeamOrRoleVisibility && (
        <Alert
          className={styles.alert}
          message={
            <>
              {intl.formatMessage(messages.noAccessWarning, { b: (text) => <b>{text}</b> })}
              <ul>
                {fieldsNotSupportingPrivateVisibility.map((field) => (
                  <li key={field}>{fieldModel.getByPlatformName(field)?.displayName}</li>
                ))}
              </ul>
            </>
          }
          showIcon
          type="warning"
        />
      )}
    </RadioGroup>
  );
};

export default VisibilityRadioGroup;
